import Phaser from 'phaser';
import { BIOMES, type BiomeDef } from '../../content/biomes';
import { COSMETICS, FINDABLE_COSMETICS } from '../../content/cosmetics';
import { CAMPAIGN_LEVELS } from '../../content/levels/campaign';
import { MOWER_IDS } from '../../content/mowers';
import { campaignLevelId, mowerMastered, totalStars } from '../../meta/economy';
import { STAR, STAR_BITS } from '../../meta/save/schema';
import { endlessBest, saves } from '../records';
import { secretYards } from '../secretYards';
import { nameText } from '../../content/names';
import { onlineOn } from '../online';
import { bevelButton, drawBigStar, navyPanel } from '../ui/chrome';
import { clubOn, drawClubBar } from '../ui/clubChrome';
import { arriveSince } from '../ui/motion';
import { drawHeader, drawTabBar, HEADER_H, TABBAR_H } from '../ui/navChrome';
import { label, UI } from '../ui/text';
import { drawTrophy, drawWorldIcon, TROPHY_SIZE } from '../ui/zoneArt';

const GOLD = 0xffd23f;

/** One zone's record: lawns cleared, stars, boss beaten, every star (its prize given). */
function zoneRecord(zone: BiomeDef) {
  const save = saves().save;
  let lawns = 0;
  let stars = 0;
  for (let n = zone.levels[0]; n <= zone.levels[1]; n++) {
    const bits = save.levels[campaignLevelId(n)]?.stars ?? 0;
    if (bits & STAR.clear) lawns++;
    stars += STAR_BITS.filter((b) => bits & b).length;
  }
  const total = zone.levels[1] - zone.levels[0] + 1;
  const boss = ((save.levels[campaignLevelId(zone.levels[1])]?.stars ?? 0) & STAR.clear) !== 0;
  return { lawns, stars, total, boss, allStars: save.zoneRewards.includes(zone.id) };
}

/**
 * The trophy shelf (owner, 2026-10-05: somewhere to see progress add up): a cup for each zone,
 * gold once its boss is beaten, a medal beside it for every star; tap one for its record. Under
 * it the rare finds kept and the lifetime records (the mowers mastered among them). With online
 * play built in it is CLUB > YOU (docs/ONLINE_UI.md), with the player's online name over the shelf.
 */
export class TrophyScene extends Phaser.Scene {
  private picked = 0;

  constructor() {
    super('Trophy');
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#15102f');
    // The latest zone reached, to start with.
    const save = saves().save;
    const reached = BIOMES.filter(
      (b) => ((save.levels[campaignLevelId(b.levels[0])]?.stars ?? 0) & STAR.clear) !== 0,
    ).length;
    this.picked = Math.max(0, Math.min(BIOMES.length - 1, reached - 1));
    this.draw(true);
  }

  private draw(entering = false): void {
    for (const o of [...this.children.list]) o.destroy();
    const { width, height } = this.scale.gameSize;
    drawHeader(this, clubOn() ? 'CLUB' : 'TROPHIES', 'club');
    const bottom = height - TABBAR_H;
    let top = clubOn() ? drawClubBar(this, 'you') : HEADER_H;
    if (clubOn() && onlineOn()) top = this.drawName(top);
    const g = this.add.graphics();

    // The shelf: two rows of four cups on wooden boards.
    const cols = 4;
    const cellW = Math.floor((width - 8) / cols);
    const k = 2;
    const cupH = TROPHY_SIZE.h * k;
    const rowH = cupH + 22;
    const shelfTop = top + 8;
    BIOMES.forEach((zone, i) => {
      const mark = this.children.length;
      const rec = zoneRecord(zone);
      const x = 4 + (i % cols) * cellW;
      const y = shelfTop + Math.floor(i / cols) * rowH;
      const cg = this.add.graphics();
      if (i === this.picked) {
        cg.fillStyle(UI.panel).fillRect(x + 1, y - 3, cellW - 2, rowH - 2);
        cg.lineStyle(1, UI.accent).strokeRect(x + 1.5, y - 2.5, cellW - 3, rowH - 3);
      }
      drawTrophy(cg, Math.round(x + cellW / 2 - (TROPHY_SIZE.w * k) / 2), y, k, rec.boss);
      if (rec.boss) drawWorldIcon(cg, zone.id, Math.round(x + cellW / 2) - 4, y + 2);
      if (rec.allStars) this.medal(cg, x + cellW - 9, y + 4);
      // The board under the cups, and the zone's star count on it.
      cg.fillStyle(0x6b4423).fillRect(x, y + cupH + 2, cellW, 4);
      cg.fillStyle(0x8b5a2b).fillRect(x, y + cupH + 2, cellW, 1);
      label(
        this,
        x + cellW / 2,
        y + cupH + 9,
        `${rec.stars}/${rec.total * 3}`,
        1,
        rec.stars === rec.total * 3 ? GOLD : UI.dim,
      ).setOrigin(0.5, 0);
      this.add
        .zone(x, y - 3, cellW, rowH)
        .setOrigin(0)
        .setData('label', zone.name)
        .setInteractive({ useHandCursor: true })
        .on('pointerup', () => {
          this.picked = i;
          this.draw();
        });
      if (entering) arriveSince(this, mark, 0, 8, 40 + i * 50, 240);
    });

    // The picked zone's record.
    let y = shelfTop + Math.ceil(BIOMES.length / cols) * rowH + 2;
    const zone = BIOMES[this.picked]!;
    const rec = zoneRecord(zone);
    const panelH = 40;
    navyPanel(g, 4, y, width - 8, panelH);
    label(this, 10, y + 6, zone.name.toUpperCase(), 1, UI.accent);
    label(this, 10, y + 17, `Lawns ${rec.lawns}/${rec.total}`, 1, UI.text);
    drawBigStar(g, 82, y + 17, true);
    label(this, 92, y + 17, `${rec.stars}/${rec.total * 3}`, 1, UI.text);
    const prize = COSMETICS.find((c) => c.zone === zone.id);
    label(
      this,
      10,
      y + 27,
      rec.allStars
        ? `Prize won: ${prize?.name ?? ''}`
        : rec.boss
          ? 'Every star wins a prize.'
          : `Boss: lawn ${zone.levels[1]}`,
      1,
      rec.allStars ? GOLD : UI.dim,
    );
    y += panelH + 6;

    // Records: two columns of lifetime numbers.
    const save = saves().save;
    const rare = FINDABLE_COSMETICS;
    const found = rare.filter((c) => save.rareFinds.includes(c.id)).length;
    const records: [string, string][] = [
      ['Lawns', String(save.stats.lawnsMowed)],
      ['Tiles', compact(save.stats.tilesMowed)],
      ['Stars', `${totalStars(save)}/${CAMPAIGN_LEVELS.length * 3}`],
      // Mowers with every upgrade bought (their gold Master trim). Owner, 2026-11-04: "Masters" did
      // not say what it counted. (A label here holds 10 characters beside its value.)
      [
        'Max mowers',
        `${MOWER_IDS.filter((id) => mowerMastered(save, id)).length}/${MOWER_IDS.length}`,
      ],
      ['Rares', `${found}/${rare.length}`],
      ['Yards', `${save.secretYards.length}/${secretYards().size}`],
      ['Streak', String(save.daily.streak.count)],
      ['Endless', String(endlessBest('lawns'))],
      // Live races (docs/LIVE.md), where online play is built in.
      ...(clubOn()
        ? ([
            ['Races', String(save.race.races)],
            ['Race wins', String(save.race.wins)],
          ] as [string, string][])
        : []),
    ];
    const rowsLeft = Math.floor((bottom - 4 - y - 12) / 10);
    if (rowsLeft < 1) {
      drawTabBar(this, 'club');
      return;
    }
    label(this, 6, y, 'RECORDS', 1, UI.dim);
    y += 12;
    const shown = records.slice(0, rowsLeft * 2);
    // Two columns with a gutter, each value right-aligned to its column.
    const gutter = 12;
    const colW = Math.floor((width - 12 - gutter) / 2);
    shown.forEach(([name, value], i) => {
      const cx = 6 + (i % 2) * (colW + gutter);
      const cy = y + Math.floor(i / 2) * 10;
      label(this, cx, cy, name, 1, UI.dim);
      label(this, cx + colW, cy, value, 1, UI.text).setOrigin(1, 0);
    });
    g.fillStyle(UI.panelHi).fillRect(
      6 + colW + gutter / 2,
      y,
      1,
      Math.ceil(shown.length / 2) * 10 - 3,
    );
    drawTabBar(this, 'club');
  }

  /**
   * The player's online name with RENAME, and FRIENDS (online scores on). Returns the y under
   * them.
   */
  private drawName(top: number): number {
    const { width } = this.scale.gameSize;
    const name = saves().save.online.player?.name;
    const y = top + 3;
    label(this, 6, y + 4, name ? nameText(name) : 'No name yet', 1, name ? UI.text : UI.dim);
    bevelButton(this, width - 54, y, 48, 14, name ? 'RENAME' : 'PICK', () =>
      this.scene.start('Name'),
    );
    bevelButton(this, 6, y + 17, width - 12, 14, 'FRIENDS', () => this.scene.start('Friends'));
    return y + 33;
  }

  /** A small gold medal (every star in the zone). */
  private medal(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
    g.fillStyle(0x3f6fd0)
      .fillRect(x - 3, y - 6, 2, 5)
      .fillRect(x + 1, y - 6, 2, 5);
    g.fillStyle(0xc07a00).fillCircle(x, y + 1, 4);
    g.fillStyle(GOLD).fillCircle(x, y, 4);
    g.fillStyle(0xffffff).fillRect(x - 1, y - 2, 1, 1);
  }
}

/** 12,345 tiles as "12.3k" (the column is narrow). */
function compact(n: number): string {
  return n >= 10000 ? `${(n / 1000).toFixed(1)}k` : String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
